/**
 * @file creditStockEffect.js
 * @version 1.1.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-15
 * @modified 2026-10-15
 * @description What a customer credit note's line does to STOCK in Xero
 *              (Cam, 15 Oct 2026). A sales credit note (ACCRECCREDIT) line
 *              that carries a stock-tracked item's code puts the goods back
 *              into stock in Xero; one without the code is money only.
 *
 *   Each tracked line of a credit note made here is one of two kinds,
 *   stored per line (invoicing.invoice_lines.credit_kind):
 *
 *     goods_returned    the item code goes to Xero — the goods come back
 *                       into stock (what every credit did before this).
 *     price_correction  NO item code goes to Xero — the account only, the
 *                       code kept in the line's words ("… [P12193]") — so
 *                       Xero moves no stock. As a supplier credit's price
 *                       correction (utils/billXeroPayload.xeroLineFor).
 *
 *   THE ASSUMPTION, per tracked line, against the invoice it credits (its
 *   line matched by item code, k-th of that code, else by words) — by
 *   QUANTITY only, prices play no part (Cam, 15 Oct 2026, 1.1.0):
 *
 *     the same quantity as the invoice line    → price correction
 *     fewer than the invoice line (and > 0)    → goods returned
 *     more than it, no matching line, no
 *     invoice to compare with, no quantity     → no assumption: the person chooses
 *
 *   It is only ever an assumption: both options are always offered. Create
 *   Invoice shows it on every save of a credit and the person confirms or
 *   switches each line; a line with none must be chosen before the save
 *   goes. The server never chooses (409 CREDIT_STOCK_KIND_REQUIRED).
 *
 *   Allocating a credit that already exists asks again, against the invoice
 *   it is being allocated to (services/creditAllocationStockService): each
 *   line starts on the kind stored at save (the invoice's suggestion said
 *   beside it when it differs), else the assumption, else nothing. A line
 *   stored with no kind (a credit from before this) sent its item code, so
 *   it counts as goods returned (storedEffect). A choice that differs from
 *   what is stored edits the credit first (allocationChanges).
 *
 *   One rule for the screen and the server: the server's
 *   utils/creditStockEffect.js is GENERATED from this file by
 *   scripts/sync-shared-utils.js — edit this one and re-run it.
 */

import { formatQuantity, formatCount } from './numberFormatters';
import { unitSuffixFor } from './units';

export const GOODS_RETURNED = 'goods_returned';
export const PRICE_CORRECTION = 'price_correction';
export const CREDIT_KINDS = [GOODS_RETURNED, PRICE_CORRECTION];

const QTY_TOLERANCE = 0.0001;

const num = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};
const text = (v) => String(v == null ? '' : v).replace(/\s+/g, ' ').trim();
const codeOf = (v) => text(v).toUpperCase();

/** A stored or requested kind as one of the two, or null. */
export function normaliseCreditKind(value) {
  const v = text(value).toLowerCase();
  if (v === GOODS_RETURNED || v === 'goods' || v === 'returned') return GOODS_RETURNED;
  if (v === PRICE_CORRECTION || v === 'price') return PRICE_CORRECTION;
  return null;
}

export const isPriceCorrection = (kind) => normaliseCreditKind(kind) === PRICE_CORRECTION;

/**
 * Does the stock choice apply to this document at all? A customer credit
 * note (not an overpayment or prepayment), not in the till's HW series and
 * not raised by the till, made here (or new). Xero-made credits keep what
 * Xero has.
 *
 * @param {{ is_credit?: boolean, transaction_type?: string, number_series?: string,
 *           invoice_no?: string, from_till?: boolean, source?: string }} doc
 */
export function stockChoiceApplies(doc) {
  if (!doc || !doc.is_credit) return false;
  const tt = text(doc.transaction_type || 'CREDIT').toUpperCase();
  if (tt !== 'CREDIT' && tt !== 'ACCRECCREDIT') return false;
  if (text(doc.number_series).toUpperCase() === 'HW') return false;
  if (/^HW/i.test(text(doc.invoice_no))) return false;
  if (doc.from_till) return false;
  if (doc.source && doc.source !== 'local') return false;
  return true;
}

/**
 * The line's words as Xero gets them on a price correction: the item code
 * kept at the end, "… [P12193]", unless the words already name it.
 */
export function withCodeInWords(description, itemCode) {
  const words = text(description);
  const code = text(itemCode);
  if (!code) return words;
  if (words.toUpperCase().includes(code.toUpperCase())) return words;
  return words ? `${words} [${code}]` : `[${code}]`;
}

/**
 * Each credit line's matching line on the original invoice(s): by item code
 * (the k-th line of that code takes the k-th original of it), else by words.
 * Returns an array the length of `lines`, each an original line or null.
 *
 * @param {Array<{ item_code?: string, description?: string }>} lines
 * @param {Array<{ item_code?: string, description?: string }>} originalLines
 */
export function matchOriginalLines(lines, originalLines) {
  const originals = Array.isArray(originalLines) ? originalLines : [];
  const used = new Set();
  const out = (Array.isArray(lines) ? lines : []).map(() => null);
  (lines || []).forEach((line, i) => {
    const code = codeOf(line && line.item_code);
    if (!code) return;
    const k = originals.findIndex((o, j) => !used.has(j) && codeOf(o && o.item_code) === code);
    if (k >= 0) { used.add(k); out[i] = originals[k]; }
  });
  (lines || []).forEach((line, i) => {
    if (out[i]) return;
    const words = text(line && line.description).toLowerCase();
    if (!words) return;
    const k = originals.findIndex((o, j) => !used.has(j) && text(o && o.description).toLowerCase() === words);
    if (k >= 0) { used.add(k); out[i] = originals[k]; }
  });
  return out;
}

/**
 * What a credit line's quantity says about it, against its invoice line —
 * the quantity alone, never the price. `{ kind, reason }` — kind null when
 * there is no assumption. The reason names the invoice ("the same quantity
 * as INV-D00012", "fewer than INV-D00012").
 */
export function assumeCreditKind(line, original) {
  if (!original) {
    return { kind: null, reason: 'no matching line on the invoice it credits' };
  }
  const inv = text(original.invoice_no) || 'the invoice';
  const q = Math.abs(num(line && line.quantity));
  const oq = Math.abs(num(original.quantity));
  if (q < QTY_TOLERANCE) return { kind: null, reason: 'no quantity on this line' };
  if (Math.abs(q - oq) < QTY_TOLERANCE) {
    return { kind: PRICE_CORRECTION, reason: `the same quantity as ${inv}` };
  }
  if (q < oq) return { kind: GOODS_RETURNED, reason: `fewer than ${inv}` };
  return { kind: null, reason: `more than ${inv} invoiced` };
}

/**
 * The rows the save asks about: one per line carrying a stock-tracked item,
 * with its original and the assumption. `kind` is the line's own choice when
 * it has one (an edit, or a choice made earlier), else the assumption.
 *
 * @param {Array} lines  request lines { item_code, description, quantity,
 *                       unit_price, credit_kind?, unit_weight_kg? }
 * @param {object} ctx
 * @param {Array} [ctx.originalLines]  the invoice(s) it credits — { item_code,
 *                       description, quantity, unit_price, invoice_no }
 * @param {(line: object, index: number) => boolean} ctx.isTracked
 * @param {(line: object, index: number) => (number|null)} [ctx.unitWeightOf]
 */
export function creditStockRows(lines, { originalLines = [], isTracked = () => false, unitWeightOf = () => null } = {}) {
  const list = Array.isArray(lines) ? lines : [];
  const matched = matchOriginalLines(list, originalLines);
  const noInvoice = !(Array.isArray(originalLines) && originalLines.length);
  const rows = [];
  list.forEach((line, index) => {
    if (!isTracked(line, index)) return;
    const original = matched[index];
    const { kind: assumed, reason } = noInvoice
      ? { kind: null, reason: 'no invoice to compare with' }
      : assumeCreditKind(line, original);
    const own = normaliseCreditKind(line && line.credit_kind);
    rows.push({
      index,
      item_code: text(line.item_code) || null,
      description: text(line.description),
      quantity: num(line.quantity),
      unit_price: num(line.unit_price),
      unit_weight_kg: unitWeightOf(line, index),
      original: original ? {
        invoice_no: original.invoice_no || null,
        quantity: num(original.quantity),
        unit_price: num(original.unit_price)
      } : null,
      assumed,
      reason,
      kind: own || assumed || null
    });
  });
  return rows;
}

/** Rows still without a kind, given the choices made (index → kind). */
export function rowsWithoutKind(rows, choices = {}) {
  return (rows || []).filter(r => !normaliseCreditKind(choices[r.index] !== undefined ? choices[r.index] : r.kind));
}

const money = (v) => `£${num(v).toFixed(2)}`;

/** "Goods returned — 10 × 25kg bag back into stock" / "Price correction — stock not changed". */
export function effectText(kind, { quantity = 0, unitWeightKg = null } = {}) {
  const k = normaliseCreditKind(kind);
  if (k === PRICE_CORRECTION) return 'Price correction — stock not changed';
  if (k === GOODS_RETURNED) {
    const suffix = unitWeightKg != null && unitWeightKg !== '' ? unitSuffixFor(unitWeightKg) : '';
    // A count of packs is a count ("40 × 25kg bag"); tonnes keep the quantity rule.
    const qty = suffix && suffix !== 'T' ? formatCount(Math.abs(num(quantity))) : formatQuantity(Math.abs(num(quantity)));
    return `Goods returned — ${qty}${suffix ? ` ${suffix}` : ''} back into stock`;
  }
  return 'Choose: goods returned or price correction';
}

/** "10 × £30.00 (INV-D00012: 10 × £32.00)" — the credit line against its invoice line. */
export function comparisonText(row) {
  if (!row) return '';
  const here = `${formatQuantity(Math.abs(num(row.quantity)))} × ${money(Math.abs(num(row.unit_price)))}`;
  if (!row.original) return `${here} (no matching invoice line)`;
  const there = `${formatQuantity(row.original.quantity)} × ${money(row.original.unit_price)}`;
  return `${here} (${row.original.invoice_no || 'invoice'}: ${there})`;
}

/** The server's refusal when a tracked line has no kind. */
export function missingKindMessage(rows) {
  const names = (rows || []).map(r => r.item_code || r.description || `line ${Number(r.index) + 1}`);
  const list = names.length > 3 ? `${names.slice(0, 3).join(', ')} and ${names.length - 3} more` : names.join(', ');
  return `Say for ${list} whether the goods came back (goods returned — back into stock in Xero) `
    + 'or only the price is being corrected (price correction — stock not changed). Nothing was saved.';
}

/**
 * What a line stored at save does in Xero now: its kind, or — none stored (a
 * credit from before the choice existed) — goods returned, since its item
 * code was sent.
 */
export const storedEffect = (kind) => normaliseCreditKind(kind) || GOODS_RETURNED;

/** "goods returned" / "price correction" — the kind in words, lower case. */
export const kindWords = (kind) => (normaliseCreditKind(kind) === PRICE_CORRECTION ? 'price correction' : 'goods returned');

/**
 * Allocating an existing credit: the kind each row will have — the choice
 * made in the window (`choices`, by line id), else the kind stored — and the
 * rows whose kind differs from what the credit does now (storedEffect), which
 * must be changed on the credit before it is allocated.
 *
 * @param {Array<{ line_id, index, item_code, description, stored }>} rows
 * @param {object} choices  line id → kind
 * @returns {{ kinds: Array<{ line_id, credit_kind }>, missing: Array, changes: Array<{ line_id, item_code, description, from, to }> }}
 */
export function allocationChoices(rows, choices = {}) {
  const kinds = [];
  const missing = [];
  const changes = [];
  (rows || []).forEach((r) => {
    const picked = choices && Object.prototype.hasOwnProperty.call(choices, r.line_id) ? choices[r.line_id] : undefined;
    const kind = normaliseCreditKind(picked) || normaliseCreditKind(r.stored);
    if (!kind) { missing.push(r); return; }
    kinds.push({ line_id: r.line_id, credit_kind: kind });
    if (kind !== storedEffect(r.stored)) {
      changes.push({ line_id: r.line_id, item_code: r.item_code || null, description: r.description || '', from: storedEffect(r.stored), to: kind });
    }
  });
  return { kinds, missing, changes };
}

/** The reason kept on the credit's edit when its stock effect is changed for an allocation. */
export function allocationReason(invoiceNos) {
  const list = (invoiceNos || []).filter(Boolean);
  return `Stock effect chosen when allocating to ${list.length ? list.join(', ') : 'an invoice'}`;
}

/** "P12193: goods returned → price correction" — a change, in words. */
export function changeText(change) {
  if (!change) return '';
  const name = change.item_code || change.description || 'line';
  return `${name}: ${kindWords(change.from)} → ${kindWords(change.to)}`;
}
